import { FileSignature, Hourglass, PlayCircle, ShoppingCart } from 'lucide-react';
import type { TraderPendingActivation } from '@pnl/shared';
import { Card } from './Card';
import { ReplicatorCard } from './ReplicatorCard';

/**
 * Dashboard de quem AINDA NÃO TEM conta ativa.
 *
 * Antes essa situação mostrava só uma caixinha "Nenhuma conta ativa encontrada"
 * no meio da tela — quem acabou de comprar entrava na plataforma e via um vazio
 * (pedido do cliente: mostrar o dashboard completo desde o primeiro acesso).
 *
 * REGRA: nenhum número é inventado. Sem conta ativada não existe saldo, equity
 * nem objetivo, então cada card aparece no seu lugar, com a mesma geometria do
 * dashboard real, dizendo o que vai mostrar quando a conta abrir. Preencher com
 * zeros faria o trader achar que tem uma conta que ele ainda não tem.
 */

interface Props {
  /** Compras pagas aguardando ativação (vazio = ninguém comprou ainda). */
  pending: TraderPendingActivation[];
}

/** Em que passo o trader está — decide o texto do banner do topo. */
function stageOf(pending: TraderPendingActivation[]) {
  if (pending.length === 0) return 'NO_PURCHASE' as const;
  // REQUESTED vem primeiro: quem já pediu ativação está adiante de quem só
  // assinou, mesmo que o contrato tenha mudado de estado depois.
  if (pending.some((p) => p.requested)) return 'REQUESTED' as const;
  if (pending.some((p) => p.contractSigned)) return 'READY' as const;
  return 'AWAITING_CONTRACT' as const;
}

const BANNER = {
  NO_PURCHASE: {
    icon: ShoppingCart,
    title: 'Seu dashboard aparece aqui',
    body: 'Assim que você adquirir um challenge e a conta for ativada, todos os indicadores abaixo passam a mostrar seus dados em tempo real.',
    tone: 'text-ink-300',
  },
  AWAITING_CONTRACT: {
    icon: FileSignature,
    title: 'Assine o contrato para liberar a ativação',
    body: 'Sua compra está confirmada. O contrato foi enviado para o seu e-mail e também está na aba Documentos — depois de assinar, você solicita a ativação em Minhas contas.',
    tone: 'text-warning-300',
  },
  READY: {
    icon: PlayCircle,
    title: 'Tudo pronto: solicite a ativação do seu challenge',
    body: 'Contrato assinado. Vá em Minhas contas e clique em “Ativar plano” — a partir daí nossa equipe cadastra o seu acesso na plataforma de negociação.',
    tone: 'text-success-300',
  },
  REQUESTED: {
    icon: Hourglass,
    title: 'Ativação solicitada — estamos preparando sua conta',
    body: 'Recebemos seu pedido. Assim que a conta for criada você recebe um e-mail e este dashboard passa a mostrar seus números.',
    tone: 'text-brand-300',
  },
} as const;

/** Card no estado "ainda sem dados", com a altura do card real. */
function Placeholder({ title, note, height }: { title: string; note: string; height?: string }) {
  return (
    <Card title={title}>
      <div className={'grid place-items-center px-5 ' + (height ?? 'py-10')}>
        <p className="text-ink-500 max-w-xs text-center text-xs leading-relaxed">{note}</p>
      </div>
    </Card>
  );
}

export function PendingDashboard({ pending }: Props) {
  const stage = stageOf(pending);
  const { icon: Icon, title, body, tone } = BANNER[stage];

  return (
    <div className="space-y-5">
      {/* Banner do topo: o próximo passo, que muda conforme o estágio. */}
      <section className="border-ink-800 bg-ink-900 rounded-xl border p-5">
        <div className="flex items-start gap-3">
          <span className={'mt-0.5 shrink-0 ' + tone}>
            <Icon size={20} />
          </span>
          <div className="min-w-0">
            <p className="text-ink-100 text-sm font-semibold">{title}</p>
            <p className="text-ink-400 mt-1 text-sm leading-relaxed">{body}</p>
            {/* O plano comprado é dado REAL — pode aparecer. */}
            {pending.length > 0 && (
              <ul className="mt-3 space-y-1">
                {pending.map((p) => (
                  <li key={p.subscriptionId} className="text-ink-300 text-xs">
                    <span className="font-semibold">{p.planName}</span> · {p.marketCode}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Mesma grade do dashboard real, pra a tela não mudar de forma quando a
          conta abrir. */}
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <Placeholder
            title="Resultados atuais"
            note="Balance, equidade e PnL do dia aparecem aqui assim que a conta for ativada."
          />
          <Placeholder
            title="Curva de patrimônio"
            note="O gráfico da sua evolução começa a ser desenhado a partir da primeira operação."
            height="h-[280px]"
          />
        </div>
        <Placeholder
          title="Dados da conta"
          note="Tamanho da conta, fase, plataforma e datas ficam nesta ficha."
        />
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Placeholder
          title="Pontuação de Disciplina"
          note="Mede a consistência do seu trading. Começa a valer na primeira operação."
        />
        {/* O card do replicador já sabe se virar sem conta funded. */}
        <ReplicatorCard />
      </div>

      <Placeholder
        title="Objetivos"
        note="Meta de lucro, perda máxima e regra do melhor dia — acompanhados automaticamente durante o challenge."
      />

      <Placeholder
        title="Posições abertas"
        note="Suas posições em aberto aparecem aqui, atualizadas após a auditoria diária."
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,460px)_1fr]">
        <Placeholder
          title="Estatísticas"
          note="Taxa de acerto, média de lucros, fator de lucro e demais números do seu histórico."
        />
        <Placeholder
          title="Resumo Diário"
          note="O resultado de cada dia operado fica listado aqui."
          height="lg:h-[420px] py-10"
        />
      </div>

      <Placeholder
        title="Diário de Trading"
        note="Calendário com o PnL de cada dia, posições fechadas e gráficos do período."
      />
    </div>
  );
}
